import { Box, Typography, Divider, Grid, TextField, InputAdornment, Radio, RadioGroup, FormControlLabel, Button } from "@mui/material"
import { Fragment, useState, ReactElement, useRef } from "react"
import { Controller } from "react-hook-form"
import { ReactComponent as VisaImage } from '../../assets/images/visacard.svg'
import { ReactComponent as MasterImage } from '../../assets/images/mastercard.svg'
import { ReactComponent as AmericanImage } from '../../assets/images/americanexpress.svg'

type InvoiceItem = {
  description: string,
  charge_type: string,
  original_amount: string | null,
  original_amount_gst: string | null,
  discount_amount: string | null,
  discount_amount_gst: string | null,
  final_amount: string | null,
  period_from: string | null,
  period_to: string | null,
}

type UpfrontPayment = {
  upfront_invoice_id: number,
  upfront_amount: string,
  surcharge_rates: {
    visa: number,
    mastercard: number,
    amex: number
  },
  invoice_items?: InvoiceItem[]
}

const VerificationPayment = ({ control, errors, setValue, loading, upfrontPayment, upfrontPaymentMade }: { control: any, errors: any, setValue: any, loading: boolean, upfrontPayment?: UpfrontPayment, upfrontPaymentMade?: boolean }) => {

  const [addDifferentMethod, setAddDifferentMethod] = useState(false)
  const [monthlyPaymentType, setMonthlyPaymentType] = useState<'credit_card' | 'bank_account'>('credit_card')
  const [creditCardTypeImg, setCreditCardTypeImg] = useState<ReactElement | null>(null)
  const [cardType, setCardType] = useState<string | null>(null)
  const valueRef = useRef<HTMLInputElement>()

  const handleRadioChange = (different: boolean) => {
    setAddDifferentMethod(different)
    if (!different) {
      setValue('payment_type', 'credit_card')
    } else {
      setValue('payment_type', monthlyPaymentType === 'credit_card' ? 'monthly_credit_card' : 'bank_account')
    }
  }

  const handleMonthlyTypeChange = (type: 'credit_card' | 'bank_account') => {
    setMonthlyPaymentType(type)
    setValue('payment_type', type === 'credit_card' ? 'monthly_credit_card' : 'bank_account')
  }

  const onChangeMonth = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target?.value;
    const maxLength = event.target?.maxLength;

    if (value.length >= maxLength) {
      valueRef?.current?.focus()
    }
  }

  const onChangeCcNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target?.value;

    if (/^3[47]/.test(value)) {
      setCreditCardTypeImg(<AmericanImage style={{ width: '100%' }} />)
      setCardType('amex')
    }
    else if (/^4/.test(value)) {
      setCreditCardTypeImg(<VisaImage style={{ width: '100%' }} />)
      setCardType('visa')
    }
    else if (/^5/.test(value)) {
      setCreditCardTypeImg(<MasterImage style={{ width: '100%' }} />)
      setCardType('mastercard')
    }
    else {
      setCreditCardTypeImg(null)
      setCardType(null)
    }
  }

  const getSurchargeRate = (): number => {
    if (!upfrontPayment || !cardType) return 0
    return upfrontPayment.surcharge_rates[cardType as keyof typeof upfrontPayment.surcharge_rates] || 0
  }

  const surchargeRate = getSurchargeRate()
  const upfrontAmount = upfrontPayment ? parseFloat(upfrontPayment.upfront_amount) : 0
  const gstTotal = upfrontPayment?.invoice_items?.reduce((sum, item) => sum + parseFloat(item.original_amount_gst ?? '0'), 0) ?? 0
  const surchargeAmount = upfrontAmount * (surchargeRate / 100)
  const totalWithSurcharge = upfrontAmount + surchargeAmount

  const labelStyle = { fontSize: '13px!important', fontWeight: 500, color: '#242424', mb: 0.5 }
  const tabStyle = (active: boolean) => ({
    fontSize: '14px!important',
    fontWeight: active ? 600 : 400,
    color: active ? '#242424' : '#4D4D4D',
    backgroundColor: active ? 'white' : 'transparent',
    borderRadius: '20px',
    px: 3,
    py: 0.8,
    textTransform: 'none' as const,
    minWidth: 'auto',
    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
    '&:hover': { backgroundColor: active ? 'white' : 'rgba(0,0,0,0.04)' },
  })

  return <Fragment>
    {!upfrontPaymentMade && upfrontPayment && <Grid item xs={12} sx={{ mb: 3 }}>
      <Grid container alignItems='center'>
        <Grid item xs={9}>
          <Typography sx={{ color: '#003187', fontWeight: 700, fontSize: '18px!important' }}>Upfront Payment Summary</Typography>
        </Grid>
        <Grid item xs={3} sx={{ textAlign: 'right' }}>
          <Typography sx={{ color: '#4D4D4D', fontWeight: 500, fontSize: '15px!important' }}>Charges</Typography>
        </Grid>
      </Grid>
      <Divider sx={{ mt: 1, mb: 2 }} />
      <Box sx={{ pl: '20px' }}>
        {upfrontPayment.invoice_items?.map((item, index) => {
          const isHeader = item.charge_type === 'subscription' || item.charge_type === 'plan_header';
          return <Grid container alignItems='center' sx={{ mt: 0.5, pl: isHeader ? 0 : '20px' }} key={index}>
            <Grid item xs={9}>
              <Typography sx={{ fontSize: '14px!important', color: '#242424' }}>
                {item.description.includes(' - ') ? <>
                  <span style={{ fontWeight: 600 }}>{item.description.split(' - ')[0]}</span> - {item.description.split(' - ').slice(1).join(' - ')}
                </> : <span style={{ fontWeight: isHeader ? 600 : 500 }}>{item.description}</span>}
              </Typography>
            </Grid>
            {item.original_amount !== null && <Grid item xs={3} sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontWeight: 500, fontSize: '14px!important', color: '#242424' }}>${parseFloat(item.original_amount).toFixed(2)}</Typography>
            </Grid>}
          </Grid>
        })}
        <Grid container alignItems='center' sx={{ mt: 0.5 }}>
          <Grid item xs={9}>
            <Typography sx={{ fontWeight: 500, fontSize: '14px!important', color: '#242424' }}>GST</Typography>
          </Grid>
          <Grid item xs={3} sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontWeight: 500, fontSize: '14px!important', color: '#242424' }}>${gstTotal.toFixed(2)}</Typography>
          </Grid>
        </Grid>
      </Box>
      <Divider sx={{ mt: 2 }} />
      <Grid container alignItems='center' sx={{ mt: 1.5, pl: '20px' }}>
        <Grid item xs={9}>
          <Typography sx={{ fontWeight: 700, fontSize: '15px!important', color: '#242424' }}>Total upfront payment</Typography>
        </Grid>
        <Grid item xs={3} sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '15px!important', color: '#242424' }}>${totalWithSurcharge.toFixed(2)}</Typography>
        </Grid>
      </Grid>
    </Grid>}

    <Grid item xs={12} sx={{ mt: 2 }}>
      {!upfrontPaymentMade ? <>
      <Typography sx={{ color: '#003187', fontWeight: 700, fontSize: '18px!important', mb: 2 }}>Payment Details</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Typography sx={labelStyle}>Card holder's name</Typography>
          <Controller
            control={control}
            name='card_holder_name'
            render={({
              field: { value, onChange },
            }) => {
              return (
                <TextField value={value} disabled={loading} error={errors.card_holder_name ? true : false} onChange={onChange} variant="outlined" size="small" sx={{ backgroundColor: 'white' }} fullWidth />
              );
            }}
          />
          {errors.card_holder_name && <Typography variant='error' sx={{ ml: 1 }}>{errors.card_holder_name.message}</Typography>}
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography sx={labelStyle}>Credit card number</Typography>
          <Controller
            control={control}
            name='card_number'
            render={({
              field: { value, onChange },
            }) => {
              return (
                <TextField
                  error={errors.card_number ? true : false}
                  fullWidth
                  disabled={loading}
                  value={value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => { onChange(e); onChangeCcNumber(e) }}
                  name='card_number'
                  size="small"
                  sx={{ backgroundColor: 'white' }}
                  InputProps={{
                    endAdornment: creditCardTypeImg ? (
                      <InputAdornment position="end">
                        <Box sx={{ display: 'flex', height: '40px', width: '40px', justifyContent: 'center', alignItems: 'center' }}>{creditCardTypeImg}</Box>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              );
            }}
          />
          {errors.card_number && <Typography variant='error' sx={{ ml: 1 }}>{errors.card_number.message}</Typography>}
        </Grid>
        <Grid item xs={12} sm={2}>
          <Typography sx={labelStyle}>Expiry date</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Controller
              control={control}
              name='card_expiry_month'
              render={({
                field: { value, onChange },
              }) => {
                return (
                  <TextField disabled={loading} type="text" variant="outlined" size="small" inputProps={{ maxLength: 2 }} error={errors.card_expiry_month ? true : false}
                    name='card_expiry_month' onChange={(e: React.ChangeEvent<HTMLInputElement>) => { onChange(e); onChangeMonth(e) }} value={value} sx={{ backgroundColor: 'white' }} placeholder="MM" />
                );
              }}
            />
            <Typography sx={{ color: '#242424' }}>/</Typography>
            <Controller
              control={control}
              name='card_expiry_year'
              render={({
                field: { value, onChange },
              }) => {
                return (
                  <TextField disabled={loading} inputRef={valueRef} type="text" variant="outlined" size="small" inputProps={{ maxLength: 2 }} error={errors.card_expiry_year ? true : false}
                    name='card_expiry_year' onChange={onChange} value={value} sx={{ backgroundColor: 'white' }} placeholder="YY" />
                );
              }}
            />
          </Box>
          {errors.card_expiry_month && <Typography variant='error' sx={{ ml: 1 }}>{errors.card_expiry_month.message}</Typography>}
          {errors.card_expiry_year && <Typography variant='error' sx={{ ml: 1 }}>{errors.card_expiry_year.message}</Typography>}
        </Grid>
        <Grid item xs={12} sm={2}>
          <Typography sx={labelStyle}>CVV</Typography>
          <Controller
            control={control}
            name='cvv'
            render={({
              field: { value, onChange },
            }) => {
              return (
                <TextField disabled={loading} type="password" variant="outlined" size="small" inputProps={{ maxLength: 4 }} error={errors.cvv ? true : false}
                  name='cvv' onChange={onChange} value={value} sx={{ backgroundColor: 'white', maxWidth: '120px' }} />
              );
            }}
          />
          {errors.cvv && <Typography variant='error' sx={{ ml: 1 }}>{errors.cvv.message}</Typography>}
        </Grid>
      </Grid>

      <Grid container sx={{ mt: 2 }} alignItems='flex-start'>
        <Grid item xs={12} sm={6}>
          <RadioGroup value={addDifferentMethod ? 'different' : 'default'} onChange={(e) => handleRadioChange(e.target.value === 'different')}>
            <FormControlLabel value="default" control={<Radio size="small" disabled={loading} />} label={<Typography sx={{ fontSize: '14px!important' }}>Make this my default payment method</Typography>} />
            <FormControlLabel value="different" control={<Radio size="small" disabled={loading} />} label={<Typography sx={{ fontSize: '14px!important' }}>Add a different payment method for monthly billing</Typography>} />
          </RadioGroup>
        </Grid>
        {upfrontPayment && <Grid item xs={12} sm={6}>
          <Typography sx={{ fontSize: '13px!important', fontWeight: 500, color: '#4D4D4D', mb: 0.5 }}>Credit card surcharges</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
            <Box sx={{ width: 24, height: 16, flexShrink: 0 }}><VisaImage style={{ width: '24px', height: '16px' }} /></Box>
            <Box sx={{ width: 24, height: 16, flexShrink: 0 }}><MasterImage style={{ width: '24px', height: '16px' }} /></Box>
            <Typography sx={{ fontSize: '13px!important', color: '#4D4D4D', ml: 0.5 }}>VISA / Mastercard  {upfrontPayment.surcharge_rates.visa}% + GST</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 24, height: 16, flexShrink: 0 }}><AmericanImage style={{ width: '24px', height: '16px' }} /></Box>
            <Typography sx={{ fontSize: '13px!important', color: '#4D4D4D', ml: 3.5 }}>Amex  {upfrontPayment.surcharge_rates.amex}% + GST</Typography>
          </Box>
        </Grid>}
      </Grid>
      </> : <Typography sx={{ color: '#003187', fontWeight: 700, fontSize: '18px!important', mb: 2 }}>Monthly Payment Method</Typography>}

      {(addDifferentMethod || upfrontPaymentMade) && <>
        <Box sx={{ mt: 3, mb: 2, display: 'inline-flex', gap: 0.5, backgroundColor: '#E8E8E8', borderRadius: '24px', p: 0.5 }}>
          <Button onClick={() => handleMonthlyTypeChange('credit_card')} sx={tabStyle(monthlyPaymentType === 'credit_card')} disabled={loading}>Credit card</Button>
          <Button onClick={() => handleMonthlyTypeChange('bank_account')} sx={tabStyle(monthlyPaymentType === 'bank_account')} disabled={loading}>Bank account</Button>
        </Box>
        {monthlyPaymentType === 'credit_card' && <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography sx={labelStyle}>Card holder's name</Typography>
            <Controller
              control={control}
              name='monthly_card_holder_name'
              render={({
                field: { value, onChange },
              }) => {
                return (
                  <TextField value={value} disabled={loading} error={errors.monthly_card_holder_name ? true : false} onChange={onChange} variant="outlined" size="small" sx={{ backgroundColor: 'white' }} fullWidth />
                );
              }}
            />
            {errors.monthly_card_holder_name && <Typography variant='error' sx={{ ml: 1 }}>{errors.monthly_card_holder_name.message}</Typography>}
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography sx={labelStyle}>Credit card number</Typography>
            <Controller
              control={control}
              name='monthly_card_number'
              render={({
                field: { value, onChange },
              }) => {
                return (
                  <TextField error={errors.monthly_card_number ? true : false} fullWidth disabled={loading} value={value} onChange={onChange} name='monthly_card_number' size="small" sx={{ backgroundColor: 'white' }} />
                );
              }}
            />
            {errors.monthly_card_number && <Typography variant='error' sx={{ ml: 1 }}>{errors.monthly_card_number.message}</Typography>}
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography sx={labelStyle}>Expiry date</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Controller
                control={control}
                name='monthly_card_expiry_month'
                render={({
                  field: { value, onChange },
                }) => {
                  return (
                    <TextField disabled={loading} type="text" variant="outlined" size="small" inputProps={{ maxLength: 2 }} error={errors.monthly_card_expiry_month ? true : false}
                      name='monthly_card_expiry_month' onChange={onChange} value={value} sx={{ backgroundColor: 'white' }} placeholder="MM" />
                  );
                }}
              />
              <Typography sx={{ color: '#242424' }}>/</Typography>
              <Controller
                control={control}
                name='monthly_card_expiry_year'
                render={({
                  field: { value, onChange },
                }) => {
                  return (
                    <TextField disabled={loading} type="text" variant="outlined" size="small" inputProps={{ maxLength: 2 }} error={errors.monthly_card_expiry_year ? true : false}
                      name='monthly_card_expiry_year' onChange={onChange} value={value} sx={{ backgroundColor: 'white' }} placeholder="YY" />
                  );
                }}
              />
            </Box>
            {errors.monthly_card_expiry_month && <Typography variant='error' sx={{ ml: 1 }}>{errors.monthly_card_expiry_month.message}</Typography>}
            {errors.monthly_card_expiry_year && <Typography variant='error' sx={{ ml: 1 }}>{errors.monthly_card_expiry_year.message}</Typography>}
          </Grid>
        </Grid>}
        {monthlyPaymentType === 'bank_account' && <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography sx={labelStyle}>Account name</Typography>
            <Controller
              control={control}
              name='bank_account_name'
              render={({
                field: { value, onChange },
              }) => {
                return (
                  <TextField disabled={loading} value={value} error={errors.bank_account_name ? true : false} onChange={onChange} variant="outlined" size="small" sx={{ backgroundColor: 'white' }} fullWidth />
                );
              }}
            />
            {errors.bank_account_name && <Typography variant='error' sx={{ ml: 1 }}>{errors.bank_account_name.message}</Typography>}
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography sx={labelStyle}>BSB</Typography>
            <Controller
              control={control}
              name='bank_account_bsb'
              render={({
                field: { value, onChange },
              }) => {
                return (
                  <TextField error={errors.bank_account_bsb ? true : false} fullWidth onChange={onChange} value={value} disabled={loading} name='bank_account_bsb' size="small" sx={{ backgroundColor: 'white' }} />
                );
              }}
            />
            {errors.bank_account_bsb && <Typography variant='error' sx={{ ml: 1 }}>{errors.bank_account_bsb.message}</Typography>}
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography sx={labelStyle}>Account number</Typography>
            <Controller
              control={control}
              name='bank_account_number'
              render={({
                field: { value, onChange },
              }) => {
                return (
                  <TextField disabled={loading} value={value} error={errors.bank_account_number ? true : false} onChange={onChange} variant="outlined" size="small" sx={{ backgroundColor: 'white' }} fullWidth />
                );
              }}
            />
            {errors.bank_account_number && <Typography variant='error' sx={{ ml: 1 }}>{errors.bank_account_number.message}</Typography>}
          </Grid>
        </Grid>}
      </>}
    </Grid>
  </Fragment>
}

export default VerificationPayment
